/**
 * tw-cart.js — the single cart layer for the custom storefront.
 *
 * The custom templates render with `layout: none`, so none of the stock theme's
 * ES modules (product-form.js, cart-icon.js, …) are available to them. This is a
 * classic script so it can be dropped into those pages directly.
 *
 * It emits `cart:update`, which is the same event name the stock theme's
 * <cart-icon> already listens for, so pages that DO use theme.liquid (the cart
 * page, for one) get their header bubble updated for free.
 *
 * Markup contract:
 *   - Any element with [data-cart-count] (or #navCartCount) is kept in sync.
 *   - Any <form data-tw-add-to-cart> is upgraded to AJAX. If the request fails
 *     the form falls back to its native POST, so add-to-cart still works when
 *     JS is unavailable or errors.
 *   - data-tw-redirect="/checkout" on the form redirects after a successful add.
 */
(function () {
  'use strict';

  var CART_UPDATE = 'cart:update';
  var CART_ERROR = 'cart:error';
  var ADDED_FEEDBACK_MS = 1400;

  function post(url, body) {
    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    }).then(function (response) {
      return response.json().then(function (data) {
        // Shopify resolves 4xx with an error body rather than rejecting.
        if (!response.ok) {
          var error = new Error(data.description || data.message || 'Cart request failed.');
          error.cart = data;
          throw error;
        }
        return data;
      });
    });
  }

  /** Writes the count into every badge on the page. */
  function renderBadges(count) {
    var badges = document.querySelectorAll('[data-cart-count], #navCartCount');

    for (var i = 0; i < badges.length; i++) {
      badges[i].textContent = count;
      if (count === 0) badges[i].setAttribute('data-zero', '');
      else badges[i].removeAttribute('data-zero');
    }
  }

  function publish(cart, source) {
    renderBadges(cart.item_count);

    document.dispatchEvent(
      new CustomEvent(CART_UPDATE, {
        bubbles: true,
        detail: {
          resource: cart,
          data: { itemCount: cart.item_count, source: source || 'tw-cart' },
        },
      })
    );

    return cart;
  }

  function fail(error) {
    document.dispatchEvent(
      new CustomEvent(CART_ERROR, { bubbles: true, detail: { message: error.message } })
    );
    throw error;
  }

  var TWCart = {
    /** @returns {Promise<object>} the current cart */
    get: function () {
      return fetch('/cart.js', { headers: { Accept: 'application/json' } }).then(function (r) {
        return r.json();
      });
    },

    /** Re-reads the cart and syncs every badge. */
    refresh: function (source) {
      return TWCart.get().then(function (cart) {
        return publish(cart, source || 'refresh');
      });
    },

    /**
     * Adds a variant to the cart.
     *
     * /cart/add.js returns the added line item, not the cart, so we re-read the
     * cart afterwards to get an authoritative item_count.
     *
     * `sections` is an optional section id. When given, Shopify returns that
     * section re-rendered, and we carry it through on the cart:update event so a
     * listener can swap it in without a second round trip. This is what lets the
     * cart page's Quick Add flip an empty cart into the filled state with no
     * page reload.
     */
    add: function (id, quantity, sections) {
      var body = { items: [{ id: parseInt(id, 10), quantity: parseInt(quantity, 10) || 1 }] };
      if (sections) body.sections = sections;

      return post('/cart/add.js', body)
        .then(function (added) {
          return TWCart.get().then(function (cart) {
            if (added && added.sections) cart.sections = added.sections;
            return publish(cart, 'tw-cart:add');
          });
        })
        .catch(fail);
    },

    /** Empties the cart. */
    clear: function () {
      return post('/cart/clear.js', {})
        .then(function (cart) {
          return publish(cart, 'tw-cart:clear');
        })
        .catch(fail);
    },

    /**
     * Starts checkout the same way the cart page's "Proceed to Checkout" button
     * does, so Razorpay Magic Checkout intercepts it.
     *
     * Razorpay's magic-shopify.js binds CLICK handlers to a selector list that
     * includes button[type="submit"][name="checkout"], and calls
     * stopImmediatePropagation() to open the Magic modal instead of letting the
     * form POST. It is gated on the shop-level oneClickCheckoutEnabled flag, not
     * on being on the cart page, so this works from a product page too.
     *
     * The click matters: form.submit() does NOT fire click handlers, so Razorpay
     * would never see it and the browser would just POST straight to Shopify's
     * own checkout — which is exactly the bug this replaces. If Razorpay is not
     * present (script blocked, feature off) the click falls through to the native
     * POST and the shopper still reaches Shopify checkout.
     */
    checkout: function () {
      var existing = document.getElementById('tw-checkout-proxy');
      if (existing) existing.remove();

      var form = document.createElement('form');
      form.id = 'tw-checkout-proxy';
      form.method = 'post';
      form.action = '/cart';
      // Off-screen rather than display:none, so the button is still a rendered
      // element — a handler that skips invisible targets will still see it.
      form.setAttribute('style', 'position:absolute;left:-9999px;top:0;opacity:0;pointer-events:none;');

      var button = document.createElement('button');
      button.type = 'submit';
      button.name = 'checkout';
      button.textContent = 'Checkout';

      form.appendChild(button);
      document.body.appendChild(form);

      button.click();
    },

    /**
     * Buy Now: check out this product on its own. The cart is emptied first so
     * the shopper is not surprised by items they added earlier showing up on the
     * payment screen.
     */
    buyNow: function (id, quantity) {
      // clear() and add() already surface their own errors; let the caller's
      // .catch() fall back to a native form POST.
      return TWCart.clear()
        .then(function () {
          return TWCart.add(id, quantity);
        })
        .then(function () {
          TWCart.checkout();
        });
    },

    /**
     * Changes a line's quantity (0 removes it). `sections` is a section id to
     * re-render via the Section Rendering API; /cart/change.js returns the full
     * cart, so no follow-up read is needed.
     */
    changeLine: function (line, quantity, sections) {
      var body = { line: line, quantity: quantity };
      if (sections) body.sections = sections;

      return post('/cart/change.js', body)
        .then(function (cart) {
          return publish(cart, 'tw-cart:change');
        })
        .catch(fail);
    },
  };

  /** Brief "Added ✓" state so the shopper gets feedback at the button, not just the badge. */
  function flashAdded(button) {
    if (!button || button.dataset.twFlashing) return;

    var original = button.innerHTML;
    button.dataset.twFlashing = 'true';
    button.innerHTML = 'Added ✓';

    setTimeout(function () {
      button.innerHTML = original;
      delete button.dataset.twFlashing;
    }, ADDED_FEEDBACK_MS);
  }

  // Declarative forms: <form data-tw-add-to-cart> becomes AJAX, native POST on failure.
  document.addEventListener('submit', function (event) {
    var form = event.target.closest && event.target.closest('form[data-tw-add-to-cart]');
    if (!form) return;

    var idField = form.querySelector('[name="id"]');
    if (!idField || !idField.value) return; // let the native POST surface the problem

    event.preventDefault();

    var quantityField = form.querySelector('[name="quantity"]');
    var button = form.querySelector('[type="submit"]');
    var redirect = form.getAttribute('data-tw-redirect');
    // Opt in to a section re-render, e.g. the cart page re-rendering itself.
    var sections = form.getAttribute('data-tw-sections');

    if (button) button.disabled = true;

    TWCart.add(idField.value, quantityField ? quantityField.value : 1, sections)
      .then(function () {
        if (redirect) {
          window.location.href = redirect;
          return;
        }
        if (button) button.disabled = false;
        flashAdded(button);
      })
      .catch(function () {
        // Fall back to the full-page POST this form would have done anyway.
        form.submit();
      });
  });

  // Restoring from bfcache shows a stale badge otherwise.
  window.addEventListener('pageshow', function (event) {
    if (event.persisted) TWCart.refresh('pageshow');
  });

  window.TWCart = TWCart;

  // Back-compat: the custom templates each used to define their own copy of this.
  window.updateNavCartCount = function () {
    return TWCart.refresh('legacy');
  };
})();
